import { useState } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useFragment } from "react-relay"
import { zodResolver } from "@hookform/resolvers/zod"
import { TrashSimple } from "@phosphor-icons/react"
import { useDirtyDrawerMaker } from "@/components/drawer-wrapper"
import { Button } from "@/components/ui/button"
import { DeprecatedConfirmationDialog } from "@/components/ui/deprecated-confirmation-dialog"
import {
  Drawer,
  DrawerBody,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer"
import { FormField } from "@/components/ui/form"
import { Input, TextArea } from "@/components/ui/input"
import {
  LabelAndSelectValue,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { FaroInsightsWrapper } from "@/grafana-faro"
import { withErrorBoundary } from "@/hoc/with-error-boundary"
import { toast } from "@/hooks/useToast"
import { textAreaDefaultMaxLength } from "@/models/common"
import {
  GeneralAttestationForm,
  generalAttestationSchema,
} from "@/modules/patients/detail/general-attestation/ch/schema/general-attestation"
import { chGeneralAttestationsFragment } from "@/modules/patients/detail/general-attestation/ch/services/chGeneralAttestationsFragment"
import { useChCreateGeneralAttestationMutation } from "@/modules/patients/detail/general-attestation/ch/services/useChCreateGeneralAttestationMutation"
import { useChDeleteGeneralAttestationMutation } from "@/modules/patients/detail/general-attestation/ch/services/useChDeleteGeneralAttestationMutation"
import { useChGenerateGeneralAttestationDocumentMutation } from "@/modules/patients/detail/general-attestation/ch/services/useChGenerateGeneralAttestationDocumentMutation"
import { useChUpdateGeneralAttestationMutation } from "@/modules/patients/detail/general-attestation/ch/services/useChUpdateGeneralAttestationMutation"
import { getPrefilledTranslations } from "@/modules/patients/detail/general-attestation/lib/get-prefilled-translations"
import { useGeneralAttestationContext } from "@/modules/patients/detail/general-attestation/store/useGeneralAttestationStore"
import { LastModificationInfo } from "@/modules/patients/detail/last-modification-info"
import { useContextAppointment } from "@/modules/patients/hooks/useContextAppointment"
import { usePatientParam } from "@/modules/patients/hooks/usePatientParam"
import {
  DOCUMENT_LANGUAGES,
  DocumentLanguage,
  getDefaultDocumentLanguageValue,
  getDocumentLanguageLabel,
} from "@/modules/patients/libs/document-languages"
import { useDrawerStore } from "@/stores/useDrawerStore"

const ChGeneralAttestationDrawer = withErrorBoundary(() => {
  const { t } = useTranslation()
  const patientId = usePatientParam()
  const contextAppointment = useContextAppointment()
  const progressNoteId = useGeneralAttestationContext((state) => state.progressNoteId)
  const generalAttestationId = useGeneralAttestationContext((state) => state.generalAttestationId)
  const setGeneralAttestationId = useGeneralAttestationContext((state) => state.setGeneralAttestationId)
  const generalAttestationRef = useGeneralAttestationContext((state) => state.generalAttestationRef)
  const closeDrawer = useDrawerStore((state) => state.closeDrawer)
  const isEditMode = !!generalAttestationId
  const isAddMode = !isEditMode && patientId && progressNoteId
  const [isOpenConfirmDialog, setIsOpenConfirmDialog] = useState(false)
  const [createGeneralAttestation, isCreating] = useChCreateGeneralAttestationMutation()
  const [deleteGeneralAttestation] = useChDeleteGeneralAttestationMutation()
  const [updateGeneralAttestation, isUpdating] = useChUpdateGeneralAttestationMutation()
  const [generateDocument, isGenerating] = useChGenerateGeneralAttestationDocumentMutation()
  const persistedData = useFragment(chGeneralAttestationsFragment, generalAttestationRef)
  const updatedAt = persistedData?.updatedAt ?? persistedData?.createdAt
  const updatedBy = persistedData?.updatedBy ?? persistedData?.createdBy
  const parsedData = isEditMode
    ? generalAttestationSchema.parse({
        ...persistedData,
        to: persistedData?.name ?? "",
      })
    : null
  const defaultLanguageValue = getDefaultDocumentLanguageValue(contextAppointment?.language)
  const {
    control,
    handleSubmit,
    register,
    getValues,
    setValue,
    formState: { errors: formErrors, isDirty },
  } = useForm<GeneralAttestationForm>({
    mode: "all",
    resolver: zodResolver(generalAttestationSchema),
    defaultValues: parsedData
      ? {
          ...parsedData,
        }
      : {
          language: defaultLanguageValue,
          ...getPrefilledTranslations(defaultLanguageValue),
        },
  })
  useDirtyDrawerMaker(isDirty)

  const handleGenerateDocument = (id: string) => {
    if (!progressNoteId) return
    generateDocument({
      params: {
        id,
      },
      onCompleted() {
        handleCloseDrawer()
      },
    })
  }

  const onSubmit = (formData: GeneralAttestationForm) => {
    if (!progressNoteId) return
    const { to, message, subject, language } = formData

    if (isAddMode)
      createGeneralAttestation({
        variables: {
          input: {
            language,
            message,
            name: to,
            subject,
            patientId,
            progressNoteId,
          },
        },
        onCompleted(response) {
          const generalAttestationId = response?.ch_createGeneralAttestation?.ch_GeneralAttestation?.id
          if (generalAttestationId) {
            handleGenerateDocument(generalAttestationId)
          }
        },
        updater(store, data) {
          try {
            const progressNoteRecordProxy = store.get(progressNoteId)
            const generalAttestation = data?.ch_createGeneralAttestation?.ch_GeneralAttestation

            if (generalAttestation?.id) {
              const newRecordProxy = store.get(generalAttestation.id)
              if (newRecordProxy) {
                progressNoteRecordProxy?.setLinkedRecord(newRecordProxy, "ch_generalAttestation")
              }
            }
          } catch (error) {
            console.error("Error while processing data:", error)
          }
        },
      })

    if (isEditMode) {
      updateGeneralAttestation({
        variables: {
          input: {
            language,
            message,
            name: to,
            subject,
            id: generalAttestationId,
          },
        },
        onCompleted(response) {
          const generalAttestationId = response.ch_updateGeneralAttestation.ch_GeneralAttestation?.id
          if (generalAttestationId) {
            handleGenerateDocument(generalAttestationId)
          }
        },
      })
    }
  }

  const handleCloseDrawer = () => {
    setGeneralAttestationId(null, null)
    closeDrawer()
    FaroInsightsWrapper.pushEvent("documents_GeneralAttestation_close")
  }

  const handleOpenConfirmDialog = (isOpen: boolean) => {
    setIsOpenConfirmDialog(isOpen)
  }

  const handleDelete = () => {
    if (progressNoteId && generalAttestationId) {
      deleteGeneralAttestation({
        variables: {
          input: {
            id: generalAttestationId,
          },
        },
        onCompleted() {
          handleCloseDrawer()
          handleOpenConfirmDialog(false)
          toast({
            description: t("patient.generalAttestation.deleteMessage.success"),
          })
        },
      })
    }
  }

  const handlePrefillGeneralAttestation = (selectedLanguage: DocumentLanguage) => {
    const prevLanguage = getValues("language")
    const { to: prevTo, message: prevMessage, subject: prevSubject } = getPrefilledTranslations(prevLanguage)
    const { to, message, subject } = getPrefilledTranslations(selectedLanguage)

    if (getValues("to") === prevTo) {
      setValue("to", to, { shouldValidate: true })
    }
    if (getValues("message") === prevMessage) {
      setValue("message", message, {
        shouldValidate: true,
      })
    }
    if (getValues("subject") === prevSubject) {
      setValue("subject", subject, {
        shouldValidate: true,
      })
    }
  }

  return (
    <>
      <Drawer open onOpenChange={handleCloseDrawer}>
        <DrawerContent>
          <DrawerClose className="top-2 right-2" />
          <DrawerHeader className="pb-4" data-testid="ch-general-attestation-header">
            <DrawerTitle className="animate-fade-in-bottom text-on-surface-subdued text-sm font-medium">
              {t("episodeOfCare.progressNote.generalAttestation")}
            </DrawerTitle>
          </DrawerHeader>
          <DrawerBody data-testid="ch-general-attestation-body">
            <FormField
              control={control}
              name="language"
              render={({ field: { onChange, value, ref } }) => (
                <div>
                  <Select
                    value={value}
                    onValueChange={(language: DocumentLanguage) => {
                      handlePrefillGeneralAttestation(language)
                      onChange(language)
                    }}
                    {...ref}>
                    <SelectTrigger className="w-56.5">
                      <SelectValue asChild>
                        <LabelAndSelectValue
                          data-testid="document-language"
                          label={t("patient.therapyPrescription.documentLanguage")}
                          value={t(getDocumentLanguageLabel(value))}
                        />
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {DOCUMENT_LANGUAGES.map((language) => (
                        <SelectItem
                          value={language.value}
                          key={language.value}
                          data-testid={`document-language-${language.value.toLocaleLowerCase()}`}>
                          {t(language.label)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            />
            <div className="flex flex-col gap-4 pt-8.75">
              <label className="text-on-surface text-base font-medium">{t("patient.generalAttestation.to")}</label>
              <Input
                {...register("to")}
                testId="general-attestation-to"
                error={formErrors.to?.message?.toString()}
                translateError={true}
                label={t("patient.generalAttestation.to")}
                required
              />
            </div>
            <div className="flex flex-col gap-4 pt-8.75">
              <label className="text-on-surface text-base font-medium">{t("patient.generalAttestation.message")}</label>
              <div>
                <Input
                  {...register("subject")}
                  testId="general-attestation-subject"
                  error={formErrors.subject?.message?.toString()}
                  translateError={true}
                  label={t("patient.generalAttestation.subject")}
                  required
                />
              </div>
              <div>
                <TextArea
                  {...register("message")}
                  data-testid="general-attestation-message"
                  error={t(formErrors.message?.message ?? "")}
                  label={t("patient.generalAttestation.message")}
                  required
                  textLimit={textAreaDefaultMaxLength}
                />
              </div>
            </div>
          </DrawerBody>
          <DrawerFooter className="flex-wrap gap-2" data-testid="ch-general-attestation-footer">
            {isEditMode && (
              <div className="flex gap-2">
                {updatedAt && updatedBy && (
                  <LastModificationInfo
                    data-testid="ch-general-attestation-last-modification-info"
                    updatedAt={updatedAt}
                    updatedBy={updatedBy}
                  />
                )}
                <button
                  onClick={() => {
                    FaroInsightsWrapper.pushEvent("documents_GeneralAttestation_delete")
                    handleOpenConfirmDialog(true)
                  }}
                  data-testid="general-attestation-delete-button">
                  <TrashSimple className="text-on-surface-subdued" />
                </button>
              </div>
            )}

            <div className="ml-auto flex gap-2">
              <Button variant="secondary" data-testid="general-attestation-cancel-button" onClick={handleCloseDrawer}>
                {t("common.button.cancel")}
              </Button>
              <Button
                className="min-w-16.25"
                disabled={isEditMode && !isDirty}
                onClick={() => {
                  FaroInsightsWrapper.pushEvent("documents_generalAttestation_save")
                  handleSubmit(onSubmit)()
                }}
                data-testid="general-attestation-save-button"
                isLoading={isCreating || isUpdating || isGenerating}>
                {t("common.button.save")}
              </Button>
            </div>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      <DeprecatedConfirmationDialog
        title={t("patient.generalAttestation.deleteConfirmTitle")}
        content={t("episodeOfCare.deleteProgressNote.content")}
        open={isOpenConfirmDialog}
        onConfirm={handleDelete}
        confirmVariant="danger"
        confirmText={t("common.button.delete")}
        cancelText={t("common.button.keep")}
        onOpenChange={handleOpenConfirmDialog}
      />
    </>
  )
}, "ChGeneralAttestationDrawer")

export { ChGeneralAttestationDrawer }
